import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useTranslation } from "../i18n/LocaleContext";

interface EmbeddedSoftphonePanelProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  softphoneUrl: string;
  bootstrapPayload: any;
  postToSoftphone: (type: string, payload: Record<string, unknown>) => void;
  onIframeLoad?: () => void;
  onMediaNotice?: (message: string) => void;
  // O desfecho da sonda de permissão interessa fora daqui: os `label` de enumerateDevices só
  // vêm preenchidos depois da concessão, então o observador de dispositivos espera por isto.
  onMediaPermission?: (granted: boolean) => void;
  className?: string;
}

// Erros de dispositivo, não de permissão: não há câmera/microfone, o dispositivo está
// com defeito ou em uso por outro aplicativo. Mandar o usuário liberar a permissão nesses
// casos é conselho errado — ele já a concedeu.
const DEVICE_ERROR_NAMES = new Set([
  "NotFoundError",
  "DevicesNotFoundError",
  "NotReadableError",
  "TrackStartError",
  "OverconstrainedError",
  "AbortError",
]);

const EmbeddedSoftphonePanel: React.FC<EmbeddedSoftphonePanelProps> = ({
  iframeRef,
  softphoneUrl,
  bootstrapPayload,
  postToSoftphone,
  onIframeLoad,
  onMediaNotice,
  onMediaPermission,
  className,
}) => {
  const { t } = useTranslation();
  const cardClassName = `status-card iframe-card${className ? ` ${className}` : ""}`;
  const [mediaPermission, setMediaPermission] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [isDeviceFailure, setIsDeviceFailure] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const failureMessage = isDeviceFailure
    ? t("embeddedSoftphone.deviceMessage")
    : t("embeddedSoftphone.permissionMessage");

  useEffect(() => {
    let cancelled = false;

    // A sonda existe só para disparar o prompt cedo e detectar bloqueio; quem abre os
    // dispositivos de verdade é o softphone dentro do iframe, e só no momento da chamada.
    // Por isso o stream é encerrado logo em seguida: mantê-lo vivo seguraria microfone e
    // câmera pela sessão inteira (LED da câmera aceso, disputa de dispositivo com o iframe).
    // Cronometrada porque o iframe do softphone fica represado atrás dela: com a câmera
    // presa por outro processo o navegador pode demorar a desistir, e no caminho de falha
    // são duas sondas em série. Sem o tempo no log não dá para separar a demora daqui da
    // do boot do próprio softphone — foi assim que se descartou uma suspeita de lentidão
    // que na verdade era o download do bundle.
    async function probe(constraints: MediaStreamConstraints) {
      const label = constraints.video ? "audio+video" : "audio";
      const startedAt = Date.now();
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getTracks().forEach((track) => track.stop());
        console.log(`[media] probe ${label} ok em ${Date.now() - startedAt}ms`);
      } catch (err) {
        console.log(
          `[media] probe ${label} falhou em ${Date.now() - startedAt}ms:`,
          (err as DOMException)?.name ?? err
        );
        throw err;
      }
    }

    async function requestMediaPermissions() {
      try {
        // Sempre pede microfone e câmera, mesmo que o softphone vá operar só com áudio: é
        // aqui que a concessão é obtida, e obtê-la para os dois no primeiro acesso evita que
        // o usuário seja interrompido por um pedido de permissão no meio de uma chamada no
        // dia em que ligar o vídeo na configuração do softphone.
        await probe({ audio: true, video: true });
        if (!cancelled) {
          setMediaPermission('granted');
          onMediaPermission?.(true);
        }
        return;
      } catch (err) {
        // getUserMedia é tudo-ou-nada sobre o conjunto pedido, então uma câmera ausente
        // (NotFoundError) ou com defeito (NotReadableError) derrubaria também o áudio. O
        // microfone é o que o softphone realmente precisa: tenta de novo só com ele.
        console.warn("[media] combined request failed, retrying with audio only", err);
      }

      try {
        await probe({ audio: true });
        if (!cancelled) {
          setMediaPermission('granted');
          onMediaPermission?.(true);
          onMediaNotice?.(t("embeddedSoftphone.videoUnavailable"));
        }
      } catch (err) {
        if (!cancelled) {
          const name = (err as DOMException)?.name ?? "";
          setIsDeviceFailure(DEVICE_ERROR_NAMES.has(name));
          setMediaPermission('denied');
          setDialogOpen(true);
          onMediaPermission?.(false);
        }
      }
    }

    requestMediaPermissions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mediaPermission === 'pending') {
    return (
      <article className={cardClassName}>
        <div className="card-header">
          <h2>{t("embeddedSoftphone.title")}</h2>
        </div>
        <div style={{ margin: 32, textAlign: 'center' }}>
          {t("embeddedSoftphone.requestingPermission")}
        </div>
      </article>
    );
  }

  if (mediaPermission === 'denied') {
    return (
      <>
        <article className={cardClassName}>
          <div className="card-header">
            <h2>{t("embeddedSoftphone.title")}</h2>
          </div>
          <div style={{ margin: 32, color: 'red', textAlign: 'center' }}>
            {failureMessage}
          </div>
        </article>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>
            {isDeviceFailure
              ? t("embeddedSoftphone.deviceDialogTitle")
              : t("embeddedSoftphone.dialogTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{failureMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} variant="contained" autoFocus>
              {t("embeddedSoftphone.gotItButton")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Permissão concedida
  return (
    <article className={cardClassName}>
      <iframe
        ref={iframeRef}
        className="softphone-frame"
        src={softphoneUrl}
        title="PBX softphone"
        allow="microphone *; camera *; speaker-selection *; autoplay *; screen-wake-lock *"
        onLoad={() => {
          onIframeLoad?.();
          if (bootstrapPayload) postToSoftphone("softphone.bootstrap", bootstrapPayload);
        }}
      />
    </article>
  );
};

export default EmbeddedSoftphonePanel;
